import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import QRCode from 'react-qr-code';

import {
  IHbciAccount,
  IHbciDecoupledStatus,
  IHbciRefreshTanRequired,
  IHbciTanChallenge,
} from '../Interfaces/IHbciAccount';
import {
  useCompleteHbciTanChallenge,
  usePollHbciDecoupled,
} from '../hooks/reactQuerys/HbciAccountQueries';

interface Props {
  open: boolean;
  idLegalTransaction: number;
  idHbciAccount: number;
  challenge: IHbciTanChallenge | null;
  /** Wird mit dem aktualisierten Konto aufgerufen, sobald die Bank
   *  bestätigt hat (oder mit ``null``, wenn der User abgebrochen hat). */
  onResolved: (
    account: IHbciAccount | null,
    nextChallenge?: IHbciTanChallenge
  ) => void;
  onError: (msg: string) => void;
}

const DECOUPLED_POLL_MS = 3000;

export const HbciTanChallengeDialog: React.FC<Props> = ({
  open,
  idLegalTransaction,
  idHbciAccount,
  challenge,
  onResolved,
  onError,
}) => {
  const [tan, setTan] = useState('');
  const { completeHbciTanChallenge, isPendingCompleteHbciTanChallenge } =
    useCompleteHbciTanChallenge(idLegalTransaction);
  const { pollHbciDecoupled, isPendingPollHbciDecoupled } =
    usePollHbciDecoupled(idLegalTransaction);
  const [pollErrorShown, setPollErrorShown] = useState(false);

  // Bei jeder neuen Challenge: TAN-Feld leeren
  useEffect(() => {
    setTan('');
    setPollErrorShown(false);
  }, [challenge?.idHbciTanChallenge]);

  // Decoupled-Polling im Intervall
  useEffect(() => {
    if (!open || !challenge || !challenge.isDecoupled) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const result: IHbciDecoupledStatus = await pollHbciDecoupled({
          idHbciAccount,
          idHbciTanChallenge: challenge.idHbciTanChallenge,
        });
        if (cancelled) return;
        if (result.status === 'confirmed' && result.account) {
          onResolved(result.account);
        }
      } catch (e) {
        if (!cancelled && !pollErrorShown) {
          setPollErrorShown(true);
          onError(
            e instanceof Error
              ? `App-Bestätigung fehlgeschlagen: ${e.message}`
              : 'App-Bestätigung fehlgeschlagen.'
          );
        }
      }
    };
    const handle = window.setInterval(tick, DECOUPLED_POLL_MS);
    // Sofort einmal pollen, damit der User nicht 3s warten muss
    void tick();
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [
    open,
    challenge,
    idHbciAccount,
    onResolved,
    onError,
    pollHbciDecoupled,
    pollErrorShown,
  ]);

  if (!challenge) return null;

  const isDecoupled = challenge.isDecoupled;
  const hasImage =
    !!challenge.challengeImageData && !!challenge.challengeImageMime;
  const hasHhduc = !!challenge.challengeHhduc;

  const handleSubmit = async () => {
    if (!tan.trim()) return;
    try {
      const result = await completeHbciTanChallenge({
        idHbciAccount,
        idHbciTanChallenge: challenge.idHbciTanChallenge,
        tan: tan.trim(),
      });
      const tanReq = (result as IHbciRefreshTanRequired).tanRequired;
      if (tanReq) {
        // Mehrstufig: weiteren Dialog mit der neuen Challenge
        onResolved(null, (result as IHbciRefreshTanRequired).challenge);
      } else {
        onResolved(result as IHbciAccount);
      }
    } catch (e) {
      onError(
        e instanceof Error
          ? `TAN-Eingabe fehlgeschlagen: ${e.message}`
          : 'TAN-Eingabe fehlgeschlagen.'
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => onResolved(null)}
      maxWidth='sm'
      fullWidth
    >
      <DialogTitle>
        Starke Authentifizierung erforderlich
        {challenge.methodLabel ? ` — ${challenge.methodLabel}` : ''}
      </DialogTitle>
      <DialogContent>
        {challenge.challengeText && (
          <Alert severity='info' sx={{ mb: 2 }}>
            {challenge.challengeText}
          </Alert>
        )}

        {/* photoTAN / chipTAN-QR mit Bild */}
        {hasImage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <img
              alt='TAN-Challenge'
              src={`data:${challenge.challengeImageMime};base64,${challenge.challengeImageData}`}
              style={{ maxWidth: 280, maxHeight: 280 }}
            />
          </Box>
        )}

        {/* chipTAN HHD-UC ohne Bild → wir rendern es als QR-Code */}
        {!hasImage && hasHhduc && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 2,
              bgcolor: '#fff',
              p: 2,
            }}
          >
            <QRCode value={challenge.challengeHhduc || ''} size={240} />
          </Box>
        )}

        {/* Decoupled (App-Bestaetigung): Spinner + Hinweis, kein TAN-Feld */}
        {isDecoupled ? (
          <Stack alignItems='center' spacing={1} sx={{ py: 2 }}>
            <CircularProgress />
            <Typography variant='body2'>
              Bitte bestätigen Sie den Zugriff in der Banking-App.
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Wir prüfen alle {DECOUPLED_POLL_MS / 1000}s, ob Sie schon
              bestätigt haben.
              {isPendingPollHbciDecoupled ? ' (Prüfe …)' : ''}
            </Typography>
          </Stack>
        ) : (
          <TextField
            autoFocus
            fullWidth
            label='TAN'
            value={tan}
            onChange={e => setTan(e.target.value)}
            inputProps={{
              inputMode: 'numeric',
              autoComplete: 'one-time-code',
              maxLength: 12,
            }}
            disabled={isPendingCompleteHbciTanChallenge}
            sx={{ mt: 1 }}
          />
        )}

        {challenge.expiresAt && (
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ display: 'block', mt: 2 }}
          >
            Die Challenge läuft am{' '}
            {new Date(challenge.expiresAt + 'Z').toLocaleTimeString('de-DE')}{' '}
            ab.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => onResolved(null)}
          disabled={isPendingCompleteHbciTanChallenge}
        >
          Abbrechen
        </Button>
        {!isDecoupled && (
          <Button
            variant='contained'
            onClick={handleSubmit}
            disabled={!tan.trim() || isPendingCompleteHbciTanChallenge}
            startIcon={
              isPendingCompleteHbciTanChallenge ? (
                <CircularProgress size={16} />
              ) : undefined
            }
          >
            {isPendingCompleteHbciTanChallenge ? 'Prüfe …' : 'TAN bestätigen'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
